import { defineStore } from "pinia";
import { computed, inject, onMounted, reactive, ref } from "vue";
export var ToastType;
(function (ToastType) {
    ToastType["BASE"] = "base";
    ToastType["SUCCESS"] = "success";
    ToastType["ERROR"] = "error";
    ToastType["DELETE"] = "delete";
    ToastType["FATAL_ERROR"] = "fatal_error";
})(ToastType || (ToastType = {}));
const defaultTimeout = 5000;
export const useToastNotificationStore = defineStore("toast-notifications", () => {
    const timers = ref({});
    const timersPaused = ref(false);
    const i18n = inject("i18n");
    const state = reactive({
        toastList: []
    });
    const hasPermanentToast = computed(() => state.toastList.some((toast) => toast.options?.permanent));
    const addToast = (type, message, options) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        state.toastList.push({
            id,
            type,
            message,
            options
        });
        // Start the timer for this notice
        if (!timersPaused.value && !hasPermanentToast.value) {
            const timeout = options?.timeout || defaultTimeout;
            timers.value[id] = setTimeout(() => closeToastById(id), timeout);
        }
    };
    const addSuccessNotice = (message, options) => {
        addToast(ToastType.SUCCESS, message, options);
    };
    const addErrorNotice = (message, options) => {
        addToast(ToastType.ERROR, message, options);
    };
    const addDeleteNotice = (message, options) => {
        addToast(ToastType.DELETE, message, options);
    };
    const addFatalErrorNotice = (message, secondaryMessage, options) => {
        const svgIcon = options?.svgIcon || "icon-caution";
        addToast(ToastType.FATAL_ERROR, message, { secondaryMessage, svgIcon, ...options });
    };
    const addFatalSaveErrorNotice = (error, message) => {
        const { statusCode } = error;
        if (!statusCode) {
            console.log("API error will be displayed as 400", error);
        }
        addFatalErrorNotice(`${i18n.t("Unable to save changes")} (${i18n.t("Error")} ${statusCode ?? 400})`, message ?? i18n.t("Please copy any unsaved changes and refresh the page."));
    };
    const toastHover = (hover) => {
        if (hover && !timersPaused.value) {
            // Pause
            cancelTimers();
            timersPaused.value = true;
        }
        else if (!hover && timersPaused.value && !hasPermanentToast.value) {
            // Resume
            state.toastList.forEach((toast) => {
                const timeout = toast.options?.timeout || defaultTimeout;
                timers.value[toast.id] = setTimeout(() => closeToastById(toast.id), timeout);
            });
            timersPaused.value = false;
        }
    };
    const toastClick = (toast) => {
        if (toast.options?.clickCallback) {
            toast.options.clickCallback();
        }
        closeToastById(toast.id);
    };
    const primaryAction = (toast) => {
        if (toast.options?.actionButtonCallback) {
            toast.options.actionButtonCallback();
            closeToastById(toast.id);
        }
    };
    const secondaryAction = (toast) => {
        if (toast.options?.secondaryActionButtonCallback) {
            toast.options.secondaryActionButtonCallback();
            closeToastById(toast.id);
        }
    };
    const closeToast = (toast) => {
        closeToastById(toast.id);
    };
    const closeToastById = (toastId) => {
        state.toastList = state.toastList.filter((t) => t.id !== toastId);
        const timerId = timers.value[toastId];
        if (timerId) {
            clearTimeout(timerId);
            delete timers.value.toastId;
        }
    };
    const closeAllToasts = () => {
        state.toastList = [];
        cancelTimers();
    };
    const cancelTimers = () => {
        Object.values(timers.value).forEach((timerId) => clearTimeout(timerId));
        timers.value = {};
    };
    const legacyAddNotice = function (message, cssClass, timeout, undoCallback) {
        const type = cssClass === "rc-error" ? ToastType.ERROR : ToastType.SUCCESS;
        const options = { timeout };
        if (undoCallback) {
            options.actionButtonLabel = i18n.t("Undo");
            options.actionButtonCallback = undoCallback;
        }
        addToast(type, message, options);
    };
    onMounted(() => {
        if (state.toastList.length > 0)
            state.toastList = [];
    });
    return {
        state,
        addToast,
        addSuccessNotice,
        addErrorNotice,
        addDeleteNotice,
        addFatalErrorNotice,
        addFatalSaveErrorNotice,
        toastHover,
        toastClick,
        primaryAction,
        secondaryAction,
        closeToast,
        closeAllToasts,
        legacyAddNotice
    };
});
